import { fromZonedTime } from "date-fns-tz";

const ZONED_ISO = /([zZ]|[+-]\d{2}:?\d{2})$/;

const looksLikeZonedIso = (value: string): boolean => ZONED_ISO.test(value);

/**
 * Format a UTC instant (ISO string with Z/offset) for display in an IANA zone.
 */
export function formatUtcInTimeZone(
  utcIso: string | null | undefined,
  ianaTimeZone: string,
  opts: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }
): string {
  if (!utcIso) return "";
  try {
    if (!looksLikeZonedIso(utcIso)) return String(utcIso);
    const date = new Date(utcIso);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat(undefined, { timeZone: ianaTimeZone, ...opts }).format(date);
  } catch {
    return String(utcIso);
  }
}

/**
 * UTC ISO -> `YYYY-MM-DDTHH:mm` for `<input type="datetime-local" />`
 * (wall clock in the launch's schedule timezone).
 */
export function utcIsoToDatetimeLocalValue(
  utcIso: string | null | undefined,
  ianaTimeZone: string
): string {
  if (!utcIso) return "";
  try {
    if (!looksLikeZonedIso(utcIso)) return "";
    const date = new Date(utcIso);
    if (Number.isNaN(date.getTime())) return "";
    const parts = new Intl.DateTimeFormat("sv-SE", {
      timeZone: ianaTimeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
    return parts.replace(" ", "T");
  } catch {
    return "";
  }
}

/**
 * `datetime-local` value (no timezone suffix) as wall clock in `ianaTimeZone` -> UTC ISO string.
 */
export function datetimeLocalToUtcIso(
  localDateTime: string | null | undefined,
  ianaTimeZone: string
): string | null {
  if (!localDateTime) return null;
  const tz = (ianaTimeZone || "UTC").trim() || "UTC";
  try {
    const d = fromZonedTime(localDateTime, tz);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  } catch {
    return null;
  }
}
